import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed, Search, PhoneCall } from 'lucide-react';
import { cn } from '@/lib/utils';

type CallType = 'incoming' | 'outgoing' | 'missed';
type CallStatus = 'completed' | 'missed' | 'ongoing';

interface CallLog {
  id: string;
  name: string;
  initials: string;
  type: CallType;
  status: CallStatus;
  duration?: string;
  time: string;
  date: string;
}

const mockCalls: CallLog[] = [
  { id: '1', name: 'Rahul Sharma', initials: 'RS', type: 'incoming', status: 'completed', duration: '4:32', time: '10:15 AM', date: 'Today' },
  { id: '2', name: 'Priya Mehta', initials: 'PM', type: 'outgoing', status: 'completed', duration: '1:20', time: '11:00 AM', date: 'Today' },
  { id: '3', name: 'Amit Kumar', initials: 'AK', type: 'incoming', status: 'missed', time: '12:30 PM', date: 'Today' },
  { id: '4', name: 'Sneha Joshi', initials: 'SJ', type: 'outgoing', status: 'completed', duration: '8:45', time: '3:00 PM', date: 'Yesterday' },
  { id: '5', name: 'Vikram Singh', initials: 'VS', type: 'incoming', status: 'missed', time: '4:15 PM', date: 'Yesterday' },
  { id: '6', name: 'Ravi Nair', initials: 'RN', type: 'outgoing', status: 'completed', duration: '2:10', time: '9:00 AM', date: 'Mon, Jul 27' },
];

const callTypeIcon = {
  incoming: PhoneIncoming,
  outgoing: PhoneOutgoing,
  missed: PhoneMissed,
};

const callTypeColor: Record<CallType, string> = {
  incoming: 'text-green-600',
  outgoing: 'text-blue-600',
  missed: 'text-red-500',
};

export function AdminChatCalls() {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | CallType>('all');

  const filtered = mockCalls.filter(c => {
    const matchSearch = c.name.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'all' || c.type === filter;
    return matchSearch && matchFilter;
  });

  const grouped = filtered.reduce<Record<string, CallLog[]>>((acc, call) => {
    if (!acc[call.date]) acc[call.date] = [];
    acc[call.date].push(call);
    return acc;
  }, {});

  return (
    <div className="flex h-full flex-col gap-4 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Phone className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-foreground">Calls</h1>
            <p className="text-sm text-muted-foreground">Call history and logs</p>
          </div>
        </div>
        <Button className="flex items-center gap-2">
          <PhoneCall className="h-4 w-4" />
          New Call
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search calls..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 w-56"
          />
        </div>
        <div className="flex gap-2">
          {(['all', 'incoming', 'outgoing', 'missed'] as const).map(f => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f)}
              className="capitalize"
            >
              {f}
            </Button>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card>
          <CardContent className="flex items-center gap-3 pt-4">
            <PhoneIncoming className="h-5 w-5 text-green-600" />
            <div>
              <p className="text-xl font-bold">{mockCalls.filter(c => c.type === 'incoming').length}</p>
              <p className="text-xs text-muted-foreground">Incoming</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-3 pt-4">
            <PhoneOutgoing className="h-5 w-5 text-blue-600" />
            <div>
              <p className="text-xl font-bold">{mockCalls.filter(c => c.type === 'outgoing').length}</p>
              <p className="text-xs text-muted-foreground">Outgoing</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-3 pt-4">
            <PhoneMissed className="h-5 w-5 text-red-500" />
            <div>
              <p className="text-xl font-bold">{mockCalls.filter(c => c.type === 'missed').length}</p>
              <p className="text-xs text-muted-foreground">Missed</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Call Log */}
      <div className="flex-1 space-y-4 overflow-y-auto">
        {Object.entries(grouped).map(([date, calls]) => (
          <div key={date}>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{date}</p>
            <div className="space-y-2">
              {calls.map(call => {
                const Icon = callTypeIcon[call.type];
                return (
                  <Card key={call.id} className="transition-shadow hover:shadow-sm">
                    <CardContent className="flex items-center gap-4 py-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                        {call.initials}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-foreground">{call.name}</p>
                          {call.status === 'missed' && (
                            <Badge variant="destructive" className="text-[10px]">Missed</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Icon className={cn("h-3 w-3", callTypeColor[call.type])} />
                          <span className="capitalize">{call.type}</span>
                          {call.duration && <span>· {call.duration}</span>}
                          <span>· {call.time}</span>
                        </div>
                      </div>
                      <Button variant="ghost" size="icon" className="shrink-0 text-primary hover:bg-primary/10">
                        <Phone className="h-4 w-4" />
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        ))}

        {filtered.length === 0 && (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <div className="text-center">
              <PhoneMissed className="mx-auto mb-3 h-10 w-10 opacity-30" />
              <p className="text-sm">No calls found</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
